import type { PairState } from './pair-types.js';
import type { EmoteState } from './emote-types.js';
import type { CombatEvent, CombatProfile, DamageModifiers, CombatState, CombatVisualEvent, HealthState, ProjectileState } from './combat-types.js';
import type { BehaviorSettings, BehaviorStatus, MonsterBehaviorId, MonsterTactics } from './behavior-types.js';
import type { FogState } from './fog-types.js';
import type { RestNotice, RestProposal, SafePlaceDefinition } from './rest-types.js';
import type { ClassDefinition, ClassState } from './class-types.js';
import type { CacheDefinition, Contents, ContainerState, EncounterReward, InventoryState, ItemDefinition, MerchantDefinition, MerchantState } from './supply-types.js';
export type Point = { x: number; z: number };
export type HeroId = string;
export interface TerrainRegion extends Point { width: number; depth: number }
export interface Obstacle extends TerrainRegion {
  kind: 'ruin' | 'tree' | 'bush' | 'rock' | 'water' | 'ridge' | 'cliff' | 'building' | 'structure'; height?: number;
  /** Cliffs only: elevation of the top of the steep ground. */
  rim?: number;
}
export interface MapPath { points: Point[]; width: number; kind: 'road' | 'trail' | 'cave'; rutted?: boolean }
/** Authored shaping of the continuous walkable ground. Heights may be negative for hollows. */
export interface Landform extends TerrainRegion { height: number; surface: 'grass' | 'sand' | 'rock' }
/** Level foundations and courtyards, blending into surrounding terrain over `blend` metres. */
export interface TerrainLevel extends TerrainRegion { elevation: number; blend: number }
/**
 * Ground inside `outline` raised `height` metres above the terrain beneath (negative sinks it). Its edge falls over `edge` metres
 * outwards: 0 (the default) is sheer, and a list gives each outline point its own run, varied smoothly between them. `profile`
 * (one, or one per outline point, blended between them) shapes the fall: an even S-curve (`scarp`), a rounded shoulder steepening towards the foot (`rounded`), or steepest at the rim
 * and spreading into a fan (`scree`). Wherever the fall is steeper than walkable is a cliff.
 */
export interface TerrainPlateau { outline: Point[]; height: number; edge?: number | number[]; profile?: TerrainProfile | TerrainProfile[] }
export type TerrainProfile = 'scarp' | 'rounded' | 'scree';
/**
 * Rock left standing over a cave: inside `outline` the ground is filled up to `height` metres above the natural ground (the
 * height a plateau of that height would give), roofing over whatever lies lower. The roof's top is the ground of the surface;
 * the cave level `level` (1 for the map's first cave, `MapDefinition.caves`) walks the ground beneath it instead. See
 * `shared/game/caves.ts`.
 */
export interface TerrainRoof { outline: Point[]; height: number; level: number }
/**
 * A straight incline `width` metres wide from `from` to `to`, each end at the height of the ground half its width beyond it,
 * carrying on level that far past either end. It only ever fills in, never digs: climbing, it ends on
 * the edge of the higher ground; descending into a hollow, it starts on the hollow's edge.
 */
export interface TerrainRamp {
  from: Point; to: Point; width: number;
  /** How far its sides fall away, default 1 m: a raised ramp has steep banks where it stands high. */
  bank?: number;
  /** Heights at `from` and `to`, instead of the ground's beyond each end, such as a beach laid over the floor of a cove. Either
   * may be left out to keep the ground's. */
  heights?: [number?, number?];
}
/**
 * Plateaus and ramps are applied after the shoreline and map-edge falloff, so a cliff can stand right at the shore.
 * Wherever the resulting ground is too steep to walk is a cliff: see `shared/game/cliffs.ts`.
 */
export interface MapTerrain {
  landforms: Landform[]; levels?: TerrainLevel[]; plateaus?: TerrainPlateau[]; ramps?: TerrainRamp[];
  /** Laid last, over everything else: the surface walks on top of them, their caves underneath. */
  roofs?: TerrainRoof[];
  /** The finest ground cell, in metres. */
  cellSize?: number;
  /** When set, gentle ground is drawn with cells this large and only ground near plateau edges and ramps with `cellSize` ones. */
  coarseCellSize?: number;
  /** Rolling ground: smooth noise up to `height` metres with folds about `scale` metres across, fading out towards `radius`. */
  relief?: { x: number; z: number; radius: number; height: number; scale: number; seed?: number }[];
  undulation?: number;
  /** The land carries on past the map's edges at its own height, instead of settling to sea level along them. */
  continuesBeyond?: boolean;
}
export interface MapProp extends Point {
  kind: 'lighthouse' | 'house' | 'chapel' | 'well' | 'boat' | 'wreck' | 'jetty' | 'camp' | 'sign' | 'grave' | 'cave-mouth' | 'banner' | 'cart' | 'standing-stones'
    | 'fence' | 'lamp-post' | 'haystack' | 'crates' | 'net-rack' | 'watchtower' | 'windmill' | 'stall' | 'laundry' | 'whale-bones'
    | 'cairn' | 'stalagmites' | 'torch-wall' | 'rowboat' | 'brazier' | 'obelisk' | 'scarecrow' | 'dead-tree' | 'bridge' | 'tide-pool' | 'bones'
    | 'shrine' | 'milestone' | 'beehives' | 'woodpile' | 'anchor' | 'lobster-pots' | 'buoy' | 'ruined-tower'
    | 'chest' | 'satchel' | 'potion-stall';
  rotation?: number; scale?: number; variant?: number;
  /** Inhabited: warm windows, chimney smoke. */
  lit?: boolean;
  /** Stands on a cave's floor (`MapDefinition.caves`, 1 for the first) instead of the surface. */
  level?: number;
  /** The usable prop (`GameContent.usableProps`) whose state this prop is drawn in. */
  id?: string;
}
/** Replaceable scenery data. Collision always comes from authoritative obstacles. */
export interface MapScenery {
  theme: 'sunset-coast'; seed: number;
  /** Scattered lone trees in open ground. Absent means they grow; `false` leaves the ground bare of them (the rest of the layout is unchanged). */
  trees?: boolean;
  /** Cosmetic meadow and crop cover; absent means `ribbons`. `ground` leaves the painted ground bare of it, `tufts` grows the older
   * scattered clumps instead. Reeds and the tufts of sand, shore, scree, cobble and marsh stay either way. */
  grassField?: 'ribbons' | 'cards' | 'ground' | 'tufts';
  shoreline: TerrainRegion[]; paths: MapPath[]; props: MapProp[];
  /** Water inland of the shoreline, such as fjords cut into cliffs: outlines drawn with the sea at sea level. Their blocking
   * belongs with the map's other `'water'` obstacles. */
  waters?: Point[][];
  districts: (Point & { name: string; radius: number; biome: 'meadow' | 'pine' | 'heath' | 'sand' | 'cobble' | 'farmland' | 'marsh' | 'scree' })[];
}
export interface MapDefinition {
  /** Shared ground surface. Absence preserves the flat maps; obstacles still own blocked areas. */
  terrain?: MapTerrain;
  id: string; name: string; subtitle: string; width: number; depth: number;
  obstacles: Obstacle[];
  roads: TerrainRegion[];
  landmarks?: { name: string; x: number; z: number }[];
  scenery?: MapScenery;
  /** Stationary neutral scenery actors, with no ownership, combat, collision or interaction rules. */
  civilians?: CivilianDefinition[];
  /** Ambient wildlife. Absent means no critters. */
  critters?: CritterSettings;
  /** Walkable levels under the terrain's roofs: cave `k` is level `k + 1`. Absent means the surface is the only level. */
  caves?: MapCave[];
  /**
   * Set only on the map as one cave level sees it (`levelMap` in `shared/game/caves.ts`): the ground under that level's roofs
   * is their floor, and the obstacles are that level's.
   */
  level?: number;
  /** Sunken interiors the camera looks down into, such as cave warrens. */
  interiors?: MapInterior[];
}
/** A level under roofs: its ground is the terrain beneath the roofs that name it, its walls the cliffs of that ground. */
export interface MapCave {
  name: string;
  /** Clear height over the floor: the roof's underside, where its openings are cut and light from the sky stops. */
  ceiling: number;
  /** Obstacles on the cave's floor, apart from its walls; they are on no other level. */
  obstacles: Obstacle[];
}
/**
 * A named sunken interior: the rings outlining its open floors (even-odd, so a ring inside a floor is a rock island), cut into higher ground. Presentation only: while the viewer's
 * heroes stand on one of its floors the browser sinks the rock round them, so the floors read as a plan. Rules, navigation,
 * vision and shadows keep the real ground.
 */
export interface MapInterior { name: string; floors: Point[][] }
/**
 * Replaceable population for a map's ambient wildlife. Critters are presentation only: they have no collision, targeting,
 * ownership or rules, and each browser rolls its own, so two players never need to see the same rabbit.
 */
export interface CritterSettings {
  /** Most rabbits alive around the party at once. */
  rabbits: number;
  /** Most crows alive on the ground around the party at once. */
  crows: number;
  /** Most gulls alive on the strand around the party at once. Gulls only live where the map has a shoreline. */
  gulls?: number;
  /** Most deer in the woods and sheep on the heath around the party; crabs on the strand; butterflies over flowery ground by day. */
  deer?: number; sheep?: number; crabs?: number; butterflies?: number;
  /** Game seconds between flocks crossing the camera's view high above the party, as [shortest, longest]. Gulls cross near the coast. */
  flyoverSeconds: [number, number];
  /** Places where birds are always on the wing. A flock exists only while the party is near its place. */
  haunts?: BirdHaunt[];
}
export type BirdKind = 'crow' | 'gull';
/** A flock that is always in the air over one place: wheeling around a spot (a lighthouse lantern, a graveyard) or working a stretch of coast. */
export type BirdHaunt = { kind: BirdKind; count: number; height: number; /** Angular speed in radians per second. */ rate: number } & (
  /** Circles `(x, z)` at `radius` metres, each bird a little wider than the last; `squash` flattens the circle north-south. */
  | { wheel: { x: number; z: number; radius: number; spread: number; squash?: number } }
  /** Beats back and forth along the shore near `z` (± `reach`), `offshore` metres out to sea and swinging `swing` metres further. */
  | { beat: { z: number; reach: number; offshore: number; swing: number } }
);
/**
 * A gang's mark, worn on armor and cloak: a glyph (`src/content/emblems/glyphs.ts`) in `ink`, on a `field` of `fieldColor` unless the field is
 * `none`, with `ticks` small bars beneath it (0 to 4) that tell one gang's mark from another's: ink on a field, `fieldColor` without one.
 */
export interface UnitEmblem {
  glyph: string; ink: number;
  field: 'none' | 'disc' | 'shield' | 'diamond' | 'hex'; fieldColor: number; ticks: number;
}
/**
 * A costume put together from interchangeable pieces (`client/rendering/entities/human-costume-mix.ts` lists what each value draws): the
 * lower garment and how it is cut, the groin cloth, the upper garment and where it is open, sleeves, legwear, belt, neckwear, extras and the
 * metal of the trim. Ids are free strings that content resolves, so an unknown one draws nothing. Presentation only.
 */
export interface CostumeMix {
  lower: string; length: string; slit: string; hem: string; hemTrim: string; groin: string;
  upper: string; front: string; back: string; straps: string;
  sleeves: string; legs: string; belt: string; neck: string; metal: string; extras: string[];
}
/**
 * The body sliders a look carries beside its height (`content/characters/rigs/humanoid-shape.ts` turns them into proportions and posture).
 * `weight` (gaunt to heavy), `muscle` (slight to powerful) and `frame` (narrow to broad bones) run from -1 to 1, with 0 the rig's own build;
 * `age` runs from 0 (a young adult) to 1 (elderly: shrunk, slack and hunched). Presentation only.
 */
export interface BodyShape { weight: number; muscle: number; frame: number; age: number }
/**
 * The face sliders a look carries beside its face parts (`content/characters/rigs/humanoid-face-shape.ts` turns them into the face warp).
 * The shape sliders run from -1 to 1, with 0 the chosen face as authored; `wrinkles` and `sag` (the aging layer) run from 0 (none) to 1 and,
 * when absent, follow the body's age. Presentation only.
 */
export interface FaceShape {
  length: number; width: number; jaw: number; chin: number; cheekbones: number; brow: number;
  noseSize: number; noseWidth: number; noseBridge: number; eyeSpacing: number; eyeDepth: number; eyeSize: number; lips: number;
  wrinkles: number; sag: number;
}
/** Optional presentation content; never changes combat equipment or collision size. */
export interface UnitAppearance {
  /** Clothing only. Weapons come from a combat unit's attacks. */
  archetype: 'cloth' | 'hides' | 'mail' | 'plate' | 'raider' | 'archer' | 'civilian';
  /** The armor worn, by id (`src/content/equipment/armor.ts`): picks the tier and finish of the archetype's outfit. */
  armor?: string;
  cloth: number; leather: number; metal: number; skin: number; hair: number;
  height?: number;
  /** Quadruped creatures: a tint multiplied into the coat (absent keeps the family's colouring) and the eyes' colour (they then glow). */
  coat?: number; eyes?: number;
  /** The body sliders over the rig's own build; absent or missing sliders keep it. */
  shape?: Partial<BodyShape>;
  /** The face sliders over the chosen face; absent or missing sliders keep it (wrinkles and sag then follow `shape.age`). */
  faceShape?: Partial<FaceShape>;
  ancestry?: string;
  /** Content-owned skeleton family; archetype only selects clothing/equipment. */
  rig?: string;
  outfit?: 'base' | 'equipped';
  /** Everyday garments for a `civilian` archetype (see `HUMAN_CLOTHING`); unknown or missing ids wear the tunic. */
  clothing?: string;
  /** Interchangeable head parts by slot (`face`, `hair`, `beard`); content resolves missing or unknown choices to defaults. */
  parts?: Record<string, string>;
  /** The gang's mark on the chest and cloak, for outfits that carry one (the bandits'). */
  emblem?: UnitEmblem;
  /** A civilian costume mixed from pieces; it replaces the garment `clothing` names. */
  costume?: CostumeMix;
  /** A civilian garment's second cloth: the shirt, chemise or collar under it (`human-garments.ts`); absent is undyed linen. */
  accent?: number;
  /** Small pieces over a civilian's garment (`CIVILIAN_ACCESSORIES`: shawls, straps, pendants, patches…), each in its own colour or its default role's. */
  accessories?: { id: string; color?: number }[];
}
export type CivilianActivity = 'talk' | 'drink' | 'study-map' | 'stretch' | 'warm-hands' | 'pray' | 'mend' | 'dig';
/** A place a walking civilian stops at: it stands `seconds` there, facing `rotation` if given, busy with `activity` if given (else its own). */
export interface CivilianStop extends Point { seconds: number; rotation?: number; activity?: CivilianActivity }
/**
 * A civilian's round, walked like an enemy patrol's loop: from each stop straight to the next at `speed` metres a second, back to the first after
 * the last. Content keeps the legs clear of obstacles. Where it is is a function of game time alone (`civilianPlace`), so every screen agrees.
 */
export interface CivilianWalk { stops: CivilianStop[]; speed: number;
  /** Something carried in the left hand from stop to stop and set down at each stop. */
  carry?: 'bucket' | 'basket' }
/** Content-owned residents of the coast. Idle animation and walking are a cosmetic projection of game time. */
export interface CivilianDefinition extends Point {
  id: string; name: string; rotation: number; activity: CivilianActivity;
  /** Seconds into the idle loop, allowing residents to take turns and avoid synchronized motion. Also offsets a walker's round. */
  phaseSeconds: number;
  appearance: UnitAppearance & { archetype: 'civilian' };
  /** A walker's round; its first stop is where it stands (`x`, `z`). Absent, the resident keeps its place. */
  walk?: CivilianWalk;
}
export interface UnitDefinition { factionId?: string; id: string; name: string; color: number; speed: number; radius: number; /** Under a roof, `level` 0 is the roof's top; a spawn there without one stands in the cave. */ spawn: Waypoint; profile: CombatProfile; appearance?: UnitAppearance;
  /** Carries a shield: `true` for a plain iron one, or a shield id from `src/content/equipment/armor.ts`. Its Defense is already in the profile. */
  shield?: boolean | string;
  /** A weapon held in the off hand for dual wielding (Core Rules, Dual Wielding): its attack id, among the weapons in `src/content/equipment/weapons.ts`. Never set with a shield. */
  offHand?: string;
  /** Holds a torch in the off hand (never with a shield or an off-hand weapon): presentation only, lit at night and in caves. */
  torch?: boolean }
/** A hero seat no account owns until a party takes it. Any account may take any number of free open seats. */
export const OPEN_SEAT = 'open';
export interface HeroDefinition extends UnitDefinition { id: HeroId;
  /** Seats sharing a slot belong to one account; `OPEN_SEAT` seats go to whichever account takes them; without one the seat is its own slot. */
  playerSlot?: string; startingInventory?: Contents; classId: string; level: number; armorId?: string; /** Worn and carried class equipment, for display. Weapons and armor are already in the profile. */ gear?: string[];
  /** The account's saved character this hero was built from. */
  characterId?: string; ancestryId?: string;
  /** Ancestry rules derived by the server from validated content, never supplied by a saved build. */
  relentless?: boolean;
  skedaddle?: boolean;
  elusive?: boolean;
  /** Extra Hit Dice on top of level, derived from ancestry content. */
  maxHitDiceBonus?: number;
  languages?: { id: string; name: string }[];
  /** The voice pack (`src/content/voices`) this hero speaks with. Absent takes the pack that suits the hero's body; a pack that doesn't exist is silent. */
  voice?: string;
  /** The painted portrait's id (`CharacterLook.portrait`); absent shows the class glyph. Presentation only. */
  portrait?: string;
  /** Skill bonuses by skill id (Core Rules p. 7). Recorded for future checks; no rule rolls them yet. */
  skills?: Record<string, number> }
/** Shared by deployment, restart and the creator sheet; ancestry increases capacity, not die size. */
export function heroMaxHitDice(hero: Pick<HeroDefinition, 'level' | 'maxHitDiceBonus'>): number { return hero.level + (hero.maxHitDiceBonus ?? 0); }
/** Server-owned walking loop outside this enemy's combat. Points are absolute map coordinates. */
export type EncounterDifficulty = Exclude<import('./encounter-budget.js').Difficulty, 'trivial'>;
export interface EnemyPatrol { waypoints: Point[]; walkSpeed: number; pauseSeconds: number; startDelaySeconds: number }
export interface EnemyDefinition extends UnitDefinition { kind: 'monster' | 'minion';
  /** What sort of creature this is (`bandit`), shown as an icon on its plate. Absent shows a generic marker. */
  family?: string; behavior: MonsterBehaviorId; encounterId?: string;
  /** Overrides a few settings of the `behavior` package for this one monster. */
  tactics?: Partial<MonsterTactics>;
  /** Stat-block level (GMG p. 7); fractions such as 1/3 are allowed. Minions carry none: they count as half a hero level each. */
  level?: number;
  /** The authored difficulty of this foe's encounter. Server-only: browsers learn it from a successful Assess on any member of the crew. */
  encounterDifficulty?: EncounterDifficulty;
  /** The hero that called this creature to fight for the party (a Shadowmancer's Shadow). It acts only on that hero's command and leaves when the fight ends. */
  summoner?: HeroId;
  patrol?: EnemyPatrol }
/** Supplies are optional: content without items, merchants, caches or rewards simply has no economy. `rewards` and `caches` stay on the server. */
export interface GameContent { enemySummons?: Record<string, EnemyDefinition>; factionRelationships?: import('./factions.js').FactionRelationship[]; map: MapDefinition; heroes: HeroDefinition[]; enemies: EnemyDefinition[]; items?: ItemDefinition[]; merchants?: MerchantDefinition[]; caches?: CacheDefinition[]; rewards?: EncounterReward[]; classes?: ClassDefinition[]; safePlaces?: SafePlaceDefinition[];
  readables?: import('./story-types.js').ReadableDefinition[]; readableSites?: import('./story-types.js').ReadableSite[]; usableProps?: import('./story-types.js').UsableProp[] }
/** A point on a route. `level` marks where the route crosses into another level (`UnitState.level`): from here on the unit walks it. */
export type Waypoint = Point & { level?: number };
export interface UnitState extends Point { id: string; name: string; radius: number; route: Waypoint[]; health: HealthState; combat: CombatState;
  /** Server-derived ground height on maps with terrain; horizontal coordinates remain the navigation contract. */
  elevation?: number;
  /**
   * The level the unit walks (`MapDefinition.caves`): absent on the surface. Under a roof it says whether the unit is on top of
   * the rock or in the cave beneath; elsewhere both levels share the ground, and it is only the one its route was planned on.
   */
  level?: number;
  /** Frightened sources: disadvantage while near, half movement speed toward them. */
  frightenedBy?: string[];
  /** Knocked down (Core p. 11): disadvantage on attacks, melee against it has advantage, standing costs 3 spaces of Speed. */
  prone?: boolean
  /** In a meeting with another unit (a handshake, a hug); both members carry one. Cosmetic: no rule reads it except that it keeps the pair together. */
  pair?: PairState;
  /** A solo emote under way (a wave, a bow); cosmetic like `pair`, and cleared when the unit is called away. */
  emote?: EmoteState;
  /** The way the unit faces (radians; forward is (sin, cos) on the ground), as of its last step. Absent until it has moved: it then faces `DEFAULT_HEADING`. A meeting approached from behind needs it. */
  heading?: number;
  /** The way a hero walking in a formation turns once it stands on its slot (the formation's facing), while that order holds; its heading takes it on arrival. */
  standFacing?: number }
/** Player orders a hero holds after the one under way. Shift appends; a plain order, Stop or death clears them. */
export type QueuedOrder = { kind: 'move'; target: Waypoint; /** A formation waypoint's facing, and the march its group shares (allies on one march pass through each other). */ facing?: number; march?: number } | { kind: 'attack'; targetId: string; attackId: string } | { kind: 'ability'; abilityId: string; targetId?: string; targetIds?: string[] };
/** The most orders one hero can hold waiting. */
export const MAX_QUEUED_ORDERS = 5;
export interface HeroState extends UnitState { playerSlot: string; playerName: string | null; connected: boolean; behavior: BehaviorSettings; behaviorStatus: BehaviorStatus; inventory: InventoryState; class: ClassState;
  /** While its player is away: whole seconds left to rejoin before the seat is released. */
  reconnectSeconds?: number;
  /** Hidden from every foe in the fight (Core pp. 8–9): they leave it be while others stand, and its first attack has advantage. Only while Hide is held. */
  hidden?: boolean;
  /** While Hide is held in a fight: the foes that can see it now, which keep it from hiding. */
  watchers?: string[];
  /** Orders waiting their turn, in order; absent when there are none. */
  queue?: QueuedOrder[];
  /** The party hero this one is following, until another order replaces it. Absent when it follows no one. */
  followId?: HeroId;
  /** The last thing this hero read, once they have read anything. */
  reading?: import('./story-types.js').ReadingState }
export interface EnemyState extends UnitState { kind: 'monster' | 'minion';
  /** Retreating or escaped through authoritative navigation; escaping is not a kill. */
  fleeing?: boolean;
  escaped?: boolean;
  /** Invisible: never published to clients and never a legal target until it attacks. */
  hidden?: boolean;
  /** In the party's fight (published only then): its sight is what a hiding hero must stay out of. */
  engaged?: boolean;
  /** A point it looks at instead of where its steps or target would turn it: a noise it glances toward. */
  lookAt?: Point;
  /** Present once the party has assessed this foe: its hit points are then exact, and these are the damage types it resists or is weak to. Unassessed foes show hit points only as unhurt, half or a quarter. */
  assessed?: { resistances: DamageModifiers; weaknesses: DamageModifiers };
  /** Present on every member of a crew once the party has assessed any one of them: the crew's authored difficulty, shown on its banner. */
  encounterDifficulty?: EncounterDifficulty }
export interface WorldState { /** Story flags set so far (by readings and prop uses), sorted. */ storyFlags: string[]; /** Each usable prop's state, by prop id. */ props: Record<string, string>; summonedEnemies?: EnemyDefinition[]; tick: number; gameTime: number; serverTime: number; revision: number; paused: boolean; inCombat: boolean; fog: FogState; combatLog: CombatEvent[]; visualEvents: CombatVisualEvent[]; projectiles: ProjectileState[]; containers: ContainerState[]; merchants: MerchantState[]; /** Clock minutes since the first midnight. */ clock: number; /** Clock speed multiplier; anything but 1 comes from development tools. */ timeScale: number; rest: RestProposal | null; restNotice: RestNotice | null; heroes: HeroState[]; enemies: EnemyState[] }
